import AiAgents from "../singleton/AIAgents";
import { AGENT_EVENTS, AGENT_EVENTS_EDITOR } from "../utils/agent-utils";
import { PROVIDERS } from "../utils/openAI-utils";

AFRAME.registerComponent("ai-agent", {
  schema: {
    aiName: { type: "string", default: "" },
    provider: { type: "string", default: "OpenAI" },
    assistant: { type: "string" },
    model: { type: "string", default: "" },
    endpoint: { type: "string", default: "" },
    apiKey: { type: "string", default: "" },
    inputType: { type: "string", default: "text" },
    encrypted: { type: "boolean", default: false },
    enableOnProximity: { type: "boolean", default: false },
    proximityRadius: { type: "number", default: 3 },
    userPrompts: { type: "boolean", default: false },
    welcomeText: { type: "string", default: "" },
    welcomeTextUrl: { type: "string", default: "" },
    emitEvents: { type: "boolean", default: false },
    voiceSpeechStrategy: { type: "string", default: "WebAPI" },
    leaveColliderText: { type: "string", default: "" },
    systemPrompt: { type: "string", default: "" },
    persistable: { type: "string", default: "" },
    targetLanguages: { type: "array", default: [] },
    dataSources: { type: "array", default: [] },
    enableTranslation: { type: "boolean", default: false }
  },
  init() {
    this.npcId = "";
    this.apiKey = this.data.apiKey;
    // this.mixerEl = findAncestorWithComponent(this.el, "animation-mixer");
    this.el.addEventListener("ai-message", this.aiTalk.bind(this));
    this.el.addEventListener("ai-end", this.onEndAudio.bind(this));
    this.el.addEventListener(AGENT_EVENTS.AI_SPEAK, this.onSpeak.bind(this));
    this.el.addEventListener(AGENT_EVENTS.AI_THINKING, this.onThinking.bind(this));
    this.el.addEventListener(AGENT_EVENTS.AI_FINISH_SPEAK, this.onFinishSpeak.bind(this));
    this.el.addEventListener(AGENT_EVENTS.AI_TALKING_FRAME, this.onTalking.bind(this));

    this.messageDispatch = window.APP.messageDispatch;
    this.threadId = null;
    this.audioEl = AFRAME.scenes[0].systems["npc-audio"].audioEl;
    this.el.appendChild(this.audioEl);
    this.assistantId = this.data.assistant;
    this.speakerBotUI = null;
    this.isTalking = false;
    this.recording = false;

    this.id = this.data.uuid || this.el.object3D.uuid;
    this.objectUuid = this.data.uuid || this.el.object3D.uuid;

    if (this.data.uuid) {
      this.el.object3D.uuid = this.data.uuid;
    }

    const isTargetLanguageProvider = [PROVIDERS.OPENAI_TRANSLATOR, PROVIDERS.OPENAI_TRANSCRIPTION].includes(
      this.data.provider
    );
    const shouldEnableProximity = this.data.enableOnProximity && !isTargetLanguageProvider;

    if (shouldEnableProximity) {
      this.el.setAttribute("ai-proximity-audio", { distance: this.data.proximityRadius });
    }

    // Parse targetLanguages — may arrive as array (from GLTF) or JSON string
    let parsedTargetLanguages = [];
    if (this.data.targetLanguages) {
      if (Array.isArray(this.data.targetLanguages)) {
        parsedTargetLanguages = this.data.targetLanguages;
      } else if (typeof this.data.targetLanguages === "string") {
        try {
          const parsed = JSON.parse(this.data.targetLanguages);
          parsedTargetLanguages = Array.isArray(parsed) ? parsed : [parsed];
        } catch (e) {
          parsedTargetLanguages = this.data.targetLanguages
            .split(",")
            .map(s => s.trim())
            .filter(Boolean);
        }
      }
    }

    let parsedDataSources = [];
    if (this.data.dataSources) {
      if (Array.isArray(this.data.dataSources)) {
        parsedDataSources = this.data.dataSources;
      } else if (typeof this.data.dataSources === "string") {
        try {
          const parsed = JSON.parse(this.data.dataSources);
          parsedDataSources = Array.isArray(parsed) ? parsed : [parsed];
        } catch (e) {
          parsedDataSources = this.data.dataSources
            .split(",")
            .map(s => s.trim())
            .filter(Boolean);
        }
      }
    }

    AiAgents.registerAgent({
      id: this.id,
      name: this.data.aiName,
      apiKey: this.data.apiKey,
      assistantId: this.data.assistant,
      model: this.data.model || undefined,
      enableTranslation: this.data.enableTranslation,
      endpoint: this.data.endpoint,
      inputType: this.data.inputType,
      encrypted: this.data.encrypted,
      enableOnProximity: shouldEnableProximity,
      proximityRadius: this.data.proximityRadius,
      el: this.el,
      objectUuid: this.objectUuid,
      welcomeText: this.data.welcomeText,
      welcomeTextUrl: this.data.welcomeTextUrl,
      voiceSpeechStrategy: this.data.voiceSpeechStrategy,
      provider: this.data.provider || PROVIDERS.OPEN_AI_ASSISTANT,
      systemPrompt: this.data.systemPrompt,
      persistable: this.data.persistable,
      targetLanguages: parsedTargetLanguages,
      dataSources: parsedDataSources
    });
  },
  update() {
    this.npcId = this.data.name;
    this.apiKey = this.data.apiKey;
  },
  onSpeak() {
    if (!this.data.emitEvents) return;
    this.el.object3D.dispatchEvent({
      type: AGENT_EVENTS_EDITOR.ON_AGENT_TALK_START,
      detail: { data: this.data }
    });
  },
  onThinking() {
    if (!this.data.emitEvents) return;
    this.el.object3D.dispatchEvent({
      type: AGENT_EVENTS_EDITOR.ON_AGENT_THINKING,
      detail: { data: this.data }
    });
  },

  onFinishSpeak() {
    if (!this.data.emitEvents) return;
    this.el.object3D.dispatchEvent({
      type: AGENT_EVENTS_EDITOR.ON_AGENT_TALK_END,
      detail: { data: this.data }
    });
  },

  async aiTalk(event) {
    if (!event || !event.detail) return;
    const { body: aiMessage, audio, messageDispatch, voiceMode, id, play, loadingAudio, playable, clientMessageId } = event.detail;

    if (messageDispatch !== false) {
      this.aiSendPrivateMessage(aiMessage, audio, voiceMode, id, play, loadingAudio, playable, clientMessageId);
    }

    // handle image
    // if(voiceMode && aiMessage.startsWith('https') && !audio) {
    //   const showImageAudio = "https://nextrealitydigital.blob.core.windows.net/nrd/metaverse/here-is-theresult.mp3";
    //   await this.renderImageUser(aiMessage);
    //   this.playAnyAudio(showImageAudio);
    // }

    // if(aiMessage.startsWith('https') || !audio) {
    //   return;
    // }

    // if(voiceMode) {
    //   this.mixerEl?.components["animation-mixer"].setPausedComponent(false);
    //   this.el.setAttribute("loop-animation", { paused: false });
    //   await new Promise((resolve, reject) => setTimeout(resolve, 100));
    //   await this.playSpeak(audio);
    // }
  },
  aiSendPrivateMessage(aiMessage, audio, voiceMode, id, play, loadingAudio, playable, clientMessageId) {
    const type = "private-chat";
    // let play = !!audio;
    // if(isIOS()) {
    //   play = false;
    // }

    // if(!voiceMode) {
    //   play = false;
    // }

    this.messageDispatch.dispatchEvent(
      new CustomEvent("ai-local-message-edit", {
        detail: {
          type: type,
          body: {
            message: aiMessage,
            receiver: this.npcId,
            sender: this.data.name,
            play,
            audio,
            groupId: id,
            loadingAudio,
            playable,
            clientMessageId
          }
        }
      })
    );
  },
  onEndAudio() {
    const audioEl = this.audioEl;
    if (audioEl) {
      // if(!this.el.components["loop-animation"].data.paused) {
      //   // this.mixerEl?.components["animation-mixer"].setPausedComponent(true);
      //   // this.el.components["loop-animation"].stopAnimation();
      // }
      audioEl.pause();
      this.messageDispatch.dispatchEvent(new CustomEvent("ai-cancel-play"));
    }

    const timeForNextPrompt = 2000; // 2 seconds
    setTimeout(() => {
      this.isTalking = false;
      // window.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_FINISH_SPEAK));
    }, timeForNextPrompt);
  },

  async playSpeakWelcome() {
    // if(AiAgents.status === "talking" || AiAgents.status === "thinking") return;
    if (this.data.welcomeText && this.data.userPrompts) {
      AiAgents.cancelCurrentPrompt({ id: this.id, el: this.el });
      // AiAgents.setMyLocalMessageTranscribed({
      //   messageTranscribed: this.data.welcomeText,
      //   receiver: this.id,
      //   mySessionId: window.mySessionId
      // })
      const agent = AiAgents.getAgentById(this.id);
      AiAgents.setAILoadingMessage(this.id);
      await new Promise(resolve => setTimeout(resolve, 100));
      const AILastMessage = window.getAILastMessage(this.id);
      if (AILastMessage) {
        await AiAgents.sendPrompt({
          message: this.data.welcomeText,
          agent: agent,
          messageId: AILastMessage.id,
          enableSpeech: true
        });
      }
    }
  },
  async playLeaveText() {
    if (this.data.userPrompts && this.data.leaveColliderText) {
      AiAgents.cancelCurrentPrompt({ id: this.id, el: this.el });
      // AiAgents.setMyLocalMessageTranscribed({
      //   messageTranscribed: this.data.leaveColliderText,
      //   receiver: this.id,
      //   mySessionId: window.mySessionId
      // })
      const agent = AiAgents.getAgentById(this.id);
      AiAgents.setAILoadingMessage(this.id);
      await new Promise(resolve => setTimeout(resolve, 10));
      const AILastMessage = window.getAILastMessage(this.id);
      AiAgents.sendPrompt({
        message: this.data.leaveColliderText,
        agent: agent,
        messageId: AILastMessage.id,
        enableSpeech: true
      });

      // setTimeout(() => {
      //   AiAgents.sendPrompt({
      //     message: this.data.leaveColliderText,
      //     agent: agent,
      //     messageId: AILastMessage.id
      //   });
      // }, 2000)
    }
  },
  onProximityEnter() {
    if (this.data.emitEvents) {
      this.el.object3D.dispatchEvent({
        type: AGENT_EVENTS_EDITOR.ON_USER_ENTER,
        detail: { data: this.data }
      });
    }
    this.playSpeakWelcome();
  },
  onProximityLeave() {
    this.playLeaveText();
    if (!this.data.emitEvents) return;
    this.el.object3D.dispatchEvent({
      type: AGENT_EVENTS_EDITOR.ON_USER_LEAVE,
      detail: { data: this.data }
    });
  },
  onTalking(e) {
    this.el.object3D.dispatchEvent({
      type: AGENT_EVENTS_EDITOR.ON_AGENT_TALKING,
      amplitude: e.detail.amplitude
    });
  },
  registerPersistable() {
    if (this.data.persistable) {
      AiAgents.setupPersistableInstance(this.id);
    }
  },
  remove() {
    AiAgents.unregisterAgent(this.id);
  }
});
